import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ReqData } from '../interfaces/IHttpRequest';
import { AUTHORIZATION_TYPE, DEMO, ERROR_TYPE_VIEW } from '../tokens/context.tokens';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private apiUrl = environment.apiUrl;

  private readonly http = inject(HttpClient);

  request(
    reqParams: ReqData,
    body: any = null,
    urlParam: string | number | string[] = '',
    queryParams = '',
    demo = false,
    cache = true,
    type?: 'blob' | 'json'
  ): Observable<any> {
    let headers = new HttpHeaders().set('Api-Version', environment.apiVersion);

    let reqUrl = this.apiUrl + reqParams.url + queryParams;

    // устанавливаем параметр в урл
    if (typeof urlParam == 'string') {
      if (reqUrl.includes(':param')) {
        reqUrl = reqUrl.replace(':param', urlParam === 'null' ? '' : urlParam);
      }
    } else if (typeof urlParam === 'number') {
      if (reqUrl.includes(':param')) {
        reqUrl = reqUrl.replace(':param', urlParam.toString());
      }
    } else {
      urlParam.forEach((param, index) => {
        if (index === 0) {
          if (reqUrl.includes(':param')) {
            reqUrl = reqUrl.replace(':param', param === 'null' ? '' : param);
          }
        } else if (reqUrl.includes(`:param${index}`)) {
          reqUrl = reqUrl.replace(`:param${index}`, param === 'null' ? '' : param);
        }
      });
    }

    // Если у нас есть кастомные заголовки, забираем их и устанавливаем
    if (reqParams.headers) {
      reqParams.headers.forEach((header) => {
        const key = Object.keys(header)[0];
        const value = header[key];
        headers = headers.set(key, value);
      });
    }

    const context: HttpContext = new HttpContext()
      .set(AUTHORIZATION_TYPE, reqParams.authType)
      .set(DEMO, demo)
      .set(ERROR_TYPE_VIEW, reqParams.errorType);

    return this.getHttpReq(
      reqUrl.toLowerCase(),
      body,
      reqParams.method,
      headers,
      context,
      cache,
      type
    ).pipe(debounceTime(200));
  }

  private postData(
    reqUrl: string,
    data: any,
    headers: HttpHeaders,
    context: HttpContext,
    cache: boolean
  ) {
    return this.http.post(reqUrl, data, {
      headers: headers,
      context: context,
      transferCache: cache,
    });
  }

  private putData(reqUrl: string, data: any, headers: HttpHeaders, context: HttpContext) {
    return this.http.put(reqUrl, data, { headers: headers, context: context });
  }

  private getData(
    reqUrl: string,
    headers: HttpHeaders,
    context: HttpContext,
    cache: boolean,
    type: 'blob' | 'json' = 'json'
  ) {
    return this.http.get(reqUrl, {
      headers: headers,
      context: context,
      transferCache: cache,
      responseType: type as any,
    });
  }

  private deleteData(
    reqUrl: string,
    data: any,
    headers: HttpHeaders,
    context: HttpContext,
    cache: boolean
  ) {
    const opt = {
      headers: headers,
      body: data || null,
      context: context,
      transferCache: cache,
    };
    return this.http.delete(reqUrl, opt);
  }

  private patchData(reqUrl: string, data: any, headers: HttpHeaders, context: HttpContext) {
    return this.http.patch(reqUrl, data || undefined, {
      headers: headers,
      context: context,
    });
  }

  private getHttpReq(
    reqUrl: string,
    body: any,
    method: string,
    headers: HttpHeaders,
    context: HttpContext,
    cache: boolean,
    type?: 'blob' | 'json'
  ) {
    switch (method) {
      case 'POST':
        return this.postData(reqUrl, body, headers, context, cache);
      case 'GET':
        return this.getData(reqUrl, headers, context, cache, type);
      case 'PUT':
        return this.putData(reqUrl, body, headers, context);
      case 'PATCH':
        return this.patchData(reqUrl, body, headers, context);
      case 'DELETE':
        return this.deleteData(reqUrl, body, headers, context, cache);
      default: {
        alert('Error getting request method');
        return this.getData(reqUrl, headers, context, cache);
      }
    }
  }
}
